const PSEUDO_FS_CLASS = "ads-player-pseudo-fullscreen";
const PSEUDO_FS_CONTAINER_CLASS = "ads-player-pseudo-fullscreen-container";
const BODY_LOCK_CLASS = "ads-player-fullscreen-lock";
const FS_SYNC_EVENT = "ads-player-fullscreen-sync";
const PLAYER_CONTAINER_SELECTOR = "[data-ads-player-container]";

let playerContainer = null;
let viewportListener = null;

export function notifyPlayerFullscreenChange() {
  document.dispatchEvent(new CustomEvent(FS_SYNC_EVENT));
}

export function subscribePlayerFullscreenChange(listener) {
  document.addEventListener(FS_SYNC_EVENT, listener);
  return () => document.removeEventListener(FS_SYNC_EVENT, listener);
}

export function setPlayerFullscreenContainer(element) {
  playerContainer = element ?? null;
}

export function getPlayerFullscreenContainer() {
  return playerContainer ?? document.querySelector(PLAYER_CONTAINER_SELECTOR);
}

export function isPlayerEmbedded() {
  try {
    return window.parent && window.parent !== window;
  } catch {
    return true;
  }
}

export function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

export function isPseudoFullscreen() {
  return document.documentElement.classList.contains(PSEUDO_FS_CLASS);
}

export function isPlayerFullscreenActive() {
  const fsEl = getFullscreenElement();
  if (fsEl) {
    return true;
  }
  return isPseudoFullscreen();
}

function isMobileDevice() {
  return (
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && navigator.platform === "MacIntel")
  );
}

function hasNativeFullscreenSupport(elements) {
  return elements.some(
    (el) =>
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen
  );
}

export function isPlayerFullscreenSupported({ videoEl } = {}) {
  if (isPlayerEmbedded()) return false;
  if (typeof document === "undefined") return false;

  if (videoEl?.webkitEnterFullscreen) return true;

  const targets = [
    getPlayerFullscreenContainer(),
    document.getElementById("root"),
    document.documentElement,
  ].filter(Boolean);

  const tryNative = !isMobileDevice() || Boolean(videoEl);
  if (tryNative && hasNativeFullscreenSupport(targets)) {
    return true;
  }

  return isMobileDevice();
}

function updateViewportHeight() {
  const height = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty(
    "--ads-player-vh",
    `${Math.round(height)}px`
  );
}

function startViewportTracking() {
  updateViewportHeight();
  if (!window.visualViewport || viewportListener) {
    return;
  }
  viewportListener = () => updateViewportHeight();
  window.visualViewport.addEventListener("resize", viewportListener);
  window.visualViewport.addEventListener("scroll", viewportListener);
}

function stopViewportTracking() {
  if (viewportListener && window.visualViewport) {
    window.visualViewport.removeEventListener("resize", viewportListener);
    window.visualViewport.removeEventListener("scroll", viewportListener);
  }
  viewportListener = null;
  document.documentElement.style.removeProperty("--ads-player-vh");
}

function requestNativeFullscreen(element) {
  const options = { navigationUI: "hide" };
  const req =
    element.requestFullscreen?.bind(element) ||
    element.webkitRequestFullscreen?.bind(element) ||
    element.mozRequestFullScreen?.bind(element) ||
    element.msRequestFullscreen?.bind(element);
  if (!req) return null;
  try {
    return req(options);
  } catch {
    try {
      return req();
    } catch {
      return null;
    }
  }
}

function exitNativeFullscreen() {
  const doc = document;
  const exit =
    doc.exitFullscreen?.bind(doc) ||
    doc.webkitExitFullscreen?.bind(doc) ||
    doc.mozCancelFullScreen?.bind(doc) ||
    doc.msExitFullscreen?.bind(doc);
  return exit?.() ?? null;
}

function enterPseudoFullscreen() {
  const container = getPlayerFullscreenContainer();
  document.documentElement.classList.add(PSEUDO_FS_CLASS);
  document.body.classList.add(BODY_LOCK_CLASS);
  container?.classList.add(PSEUDO_FS_CONTAINER_CLASS);
  startViewportTracking();
  window.scrollTo(0, 0);
  requestAnimationFrame(() => {
    window.scrollTo(0, 1);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  });
  notifyPlayerFullscreenChange();
}

function exitPseudoFullscreen() {
  const container = getPlayerFullscreenContainer();
  document.documentElement.classList.remove(PSEUDO_FS_CLASS);
  document.body.classList.remove(BODY_LOCK_CLASS);
  container?.classList.remove(PSEUDO_FS_CONTAINER_CLASS);
  stopViewportTracking();
  notifyPlayerFullscreenChange();
}

export function exitPlayerFullscreen() {
  if (getFullscreenElement()) {
    const promise = exitNativeFullscreen();
    return promise ? promise.catch(() => {}) : Promise.resolve();
  }
  if (isPseudoFullscreen()) {
    exitPseudoFullscreen();
  }
  return Promise.resolve();
}

export async function requestPlayerFullscreen({ videoEl } = {}) {
  const targets = [
    getPlayerFullscreenContainer(),
    document.getElementById("root"),
    document.documentElement,
  ].filter(Boolean);

  const tryNative = !isMobileDevice() || Boolean(videoEl);

  if (tryNative) {
    for (const target of targets) {
      const promise = requestNativeFullscreen(target);
      if (!promise) continue;
      try {
        await promise;
        return "native";
      } catch {
        // try next target
      }
    }
  }

  if (videoEl?.webkitEnterFullscreen) {
    try {
      videoEl.webkitEnterFullscreen();
      return "video-webkit";
    } catch {
      // fall through to pseudo fullscreen
    }
  }

  enterPseudoFullscreen();
  return "pseudo";
}
